import i18n from 'frameworks_drivers/localization/Localization';
import initABTesting from '../abTestingInitializer';

import { initFBPixel, loadFBPixel } from './fbPixel';
import { loadGtag } from './gtag';
import { GoogleAnalyticsInitializer } from './initialization/GoogleAnalyticsInitializer';
import { initLinkedInPixel, loadLinkedInPixel } from './linkedInPixel';
import {
  initProgrammaticRetargetingPixel,
  loadProgrammaticRetargetingPixel,
  PROGRAMMATIC_RETARGETING_IMG_SRC
} from './programmaticTracking';
import { TrackerUtil } from './TrackerUtil';
import { environmentService } from '../config/di/container';
import { PsoTracking } from './PsoScriptLoader';

/**
 * This service is responsible for setting up and managing all tracking related functionalities.
 * It loads all the necessary tracking scripts asynchronously, including Google Analytics (gtag), Facebook Pixel, LinkedIn Pixel, and Programmatic Retargeting Pixel.
 * It also provides methods to setup tracking and to track pages and events.
 *
 * The tracking data is managed by the TrackerUtil.
 */
const TrackingService = () => {
  /**
   * Asynchronously loads all necessary tracking scripts.
   *
   * This includes:
   * - PsoTracking
   * - Google Analytics (gtag)
   * - Facebook Pixel
   * - LinkedIn Pixel
   * - Programmatic Retargeting Pixel
   *
   * Each script is loaded and then initialized if necessary.
   *
   * @returns {Promise} A promise that resolves when all scripts have been loaded and initialized.
   */
  const loadTrackingScripts = async () => {
    // TODO: Move to app loader
    initABTesting();

    const linkedInPixelPartnerId = environmentService.getLinkedinPixelPartnerId();
    const fbPixelId = environmentService.getFbPixelId();
    const gaTrackingId = environmentService.getGaTrackingId();

    return Promise.all([
      PsoTracking.load().then(() => PsoTracking.init()),
      loadGtag(gaTrackingId),
      loadFBPixel(fbPixelId).then((initNeeded) => initFBPixel(initNeeded, fbPixelId)),
      loadLinkedInPixel(linkedInPixelPartnerId).then((initNeeded) =>
        initLinkedInPixel(initNeeded, linkedInPixelPartnerId)
      ),
      loadProgrammaticRetargetingPixel(PROGRAMMATIC_RETARGETING_IMG_SRC).then((initNeeded) =>
        initProgrammaticRetargetingPixel(initNeeded, PROGRAMMATIC_RETARGETING_IMG_SRC)
      ),
      i18n
    ]);
  };

  /**
   * Sets up tracking for Google Analytics.
   *
   * This involves creating a new instance of GoogleAnalyticsInitializer and starting it.
   */
  function setupTracking() {
    new GoogleAnalyticsInitializer().start();
  }

  /**
   * Tracks a page view.
   *
   * If a page name is provided, it is used. Otherwise, the current page name is retrieved from TrackerUtil.
   *
   * @param {string} [pageName] - The name of the page to track. If not provided, the current page name is used.
   */
  function trackPage(pageName?: string) {
    TrackerUtil.trackPage(pageName ?? TrackerUtil.getPageName());
  }

  /**
   * Tracks a specific event.
   *
   * This involves providing details about the event such as the page name, category, action, type, and an optional value.
   * These details are then passed to the TrackerUtil's trackEvent method.
   *
   * @param {string} pageName - The name of the page where the event occurred.
   * @param {string} category - The category of the event.
   * @param {string} action - The action that was performed.
   * @param {string} type - The type of the event.
   * @param {string} [value] - An optional value associated with the event.
   */
  function trackEvent(
    pageName: string,
    category: string,
    action: string,
    type: string,
    value?: string
  ) {
    TrackerUtil.trackEvent(pageName, category, action, type, value);
  }

  // Return the tracking functions so they can be used elsewhere
  return { loadTrackingScripts, setupTracking, trackPage, trackEvent };
};

export default TrackingService;
